import { useState } from 'react';
import { Info } from 'lucide-react';
import { usePredictionMarketConfigGetSections } from '@/lib/endpoints';
import { SectionResponseDtoSection } from '@/lib/models';
import type { EventDetailDto } from '@/lib/models';
import { formatVolume } from '@/lib/binary-market';
import { orderSections, type MarketSectionKey } from './order-sections';
import { isEventClosed } from './market-status';
import { CollapsibleSection } from './CollapsibleSection';
import { CommentsSection } from '../Comments/CommentsSection';
import { MarketRulesContent } from './MarketRulesContent';
import { TrendingSection } from './TrendingSection';
import { MarketOpenPositions } from './MarketOpenPositions';
import { MarketOpenOrders } from './MarketOpenOrders';
import { MarketResult } from './MarketResult';
import { MarketHistory } from './MarketHistory';
import { OutcomeOrderBook } from './OutcomeOrderBook';
import { LimitOrderPopup } from './LimitOrderPopup';
import type { MarketOptionDto } from '@/lib/models';

export interface MarketSectionsProps {
  /** The event whose market-page sections to render. */
  event: EventDetailDto;
  /** The event's tradeable options (across markets) — scope positions / history / order book. */
  options: MarketOptionDto[];
  /**
   * Markets whose winners the Result section shows. Defaults to all of the
   * event's markets; sports pages narrow it to the moneyline so the result is
   * the match winner, not every prop market's outcome.
   */
  resultMarkets?: EventDetailDto['markets'];
}

/** Human title per section key. */
const SECTION_TITLE: Record<MarketSectionKey, string> = {
  OPEN_POSITIONS: 'Open Positions',
  HISTORY: 'My History',
  ORDER_BOOK: 'Order Book',
  MARKET_RULES: 'Market Rules',
  TRENDING: 'People Are Also Trading',
  COMMENTS: 'Comments',
  OPEN_ORDERS: 'Open Orders',
  RESULT: 'Outcome',
};

/** Sections that render only while trading is live; removed on a closed market (PRD §7.5). */
const LIVE_ONLY_SECTIONS = new Set<MarketSectionKey>([
  SectionResponseDtoSection.OPEN_POSITIONS,
  SectionResponseDtoSection.OPEN_ORDERS,
  SectionResponseDtoSection.ORDER_BOOK,
]);

/**
 * Admin-ordered collapsible sections for the market page (ARC-82 §5.4 +
 * sell/limit PRD §6–7).
 *
 * Order + default-open come from `usePredictionMarketConfigGetSections`
 * (sorted by `sortOrder`, §7.1). Availability depends on the market state:
 * live pages get the trading sections (Open Positions, Open Orders, Order
 * Book) and no Result; closed pages drop the trading sections and show Result.
 * Supports the all-collapsed state (§5.4.7): every section simply starts
 * collapsed.
 */
export function MarketSections({ event, options, resultMarkets }: MarketSectionsProps) {
  const { data: config } = usePredictionMarketConfigGetSections();
  const closed = isEventClosed(event.markets);
  const resolved = orderSections(config).filter(({ section }) =>
    closed ? !LIVE_ONLY_SECTIONS.has(section) : section !== SectionResponseDtoSection.RESULT,
  );
  const [limitOpen, setLimitOpen] = useState(false);

  const orderBookVolume = event.markets.reduce((sum, m) => sum + (parseFloat(m.volume) || 0), 0);
  // Order book is per-outcome; show the first option's Yes outcome (favourite / primary).
  const primaryOption = options[0];
  const orderBookOutcomeId = primaryOption?.yes.externalOutcomeId ?? '';

  return (
    <div className="flex flex-col gap-2.5">
      {resolved.map(({ section, defaultOpen }) => {
        const title = SECTION_TITLE[section];

        switch (section) {
          case SectionResponseDtoSection.MARKET_RULES:
            return (
              <CollapsibleSection key={section} title={title} defaultOpen={defaultOpen}>
                <MarketRulesContent markets={event.markets} />
              </CollapsibleSection>
            );

          // People Are Also Trading (ARC-161) — the section hides itself
          // entirely (no header/chevron) when there are no eligible markets.
          case SectionResponseDtoSection.TRENDING:
            return <TrendingSection key={section} eventId={event.id} title={title} defaultOpen={defaultOpen} />;

          // Open Positions (§5.4.1) — the user's open positions in this event, with Sell.
          case SectionResponseDtoSection.OPEN_POSITIONS:
            return <MarketOpenPositions key={section} options={options} title={title} defaultOpen={defaultOpen} />;

          // Open Orders (sell/limit PRD §6) — resting limit orders with Modify / Cancel.
          case SectionResponseDtoSection.OPEN_ORDERS:
            return (
              <MarketOpenOrders
                key={section}
                options={options}
                eventName={event.name}
                title={title}
                defaultOpen={defaultOpen}
              />
            );

          // Result (sell/limit PRD §7.4) — the resolved outcome; closed pages only.
          case SectionResponseDtoSection.RESULT:
            return (
              <MarketResult key={section} markets={resultMarkets ?? event.markets} title={title} defaultOpen={defaultOpen} />
            );

          // History (§5.4.2) — the user's finished positions in this event.
          case SectionResponseDtoSection.HISTORY:
            return <MarketHistory key={section} markets={event.markets} title={title} defaultOpen={defaultOpen} />;

          // Order Book (§5.4.3) — live snapshot for the primary market's outcome.
          case SectionResponseDtoSection.ORDER_BOOK:
            return (
              <CollapsibleSection
                key={section}
                title={title}
                defaultOpen={defaultOpen}
                summary={
                  <span className="inline-flex items-center gap-1 text-muted">
                    ${formatVolume(String(orderBookVolume))} Vol
                    <Info className="size-3.5" />
                  </span>
                }
                trailing={
                  // Rendered inside CollapsibleSection's header <button>, so this
                  // must not be a nested <button>; stop propagation so the click
                  // opens the popup without also toggling the section.
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => {
                      e.stopPropagation();
                      setLimitOpen(true);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        e.stopPropagation();
                        setLimitOpen(true);
                      }
                    }}
                    className="text-sm text-purple underline tracking-[-0.36px] cursor-pointer"
                  >
                    Limit Order
                  </span>
                }
              >
                <OutcomeOrderBook
                  outcomeId={orderBookOutcomeId}
                  marketId={primaryOption?.marketId}
                  externalMarketId={primaryOption?.externalMarketId}
                />
              </CollapsibleSection>
            );

          // Comments (§5.4.6) — public reads, sign-in to post, KYC-gated writes.
          case SectionResponseDtoSection.COMMENTS:
            return (
              <CollapsibleSection key={section} title={title} defaultOpen={defaultOpen}>
                <CommentsSection eventId={event.id} />
              </CollapsibleSection>
            );

          default:
            return null;
        }
      })}
      <LimitOrderPopup
        market={limitOpen && primaryOption ? { ...primaryOption, eventName: event.name } : null}
        onClose={() => setLimitOpen(false)}
      />
    </div>
  );
}
